import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'

export default function AnimatePanelSecondaryBar({
  selectedElement,
  template_content,
  settemplate_content,
  onClose
}) {
  const [selectedAnimation, setSelectedAnimation] = useState(null)
  const [hoveredAnimation, setHoveredAnimation] = useState(null)
  const [appliedAnimations, setAppliedAnimations] = useState([])

  // Get currently applied animation on the selected element (only one animation allowed)
  useEffect(() => {
    if (!selectedElement || !template_content) return

    const currentElement = template_content.pages
      ?.flatMap(page => page.items || [])
      ?.find(item => item.id === selectedElement)

    if (currentElement && currentElement.animations && currentElement.animations.length > 0) {
      // Only one animation allowed, so get the first (and only) animation
      const currentAnimation = currentElement.animations[0]
      setAppliedAnimations([currentAnimation.type])
      setSelectedAnimation(currentAnimation.type)
    } else {
      setAppliedAnimations([])
      setSelectedAnimation(null)
    }
  }, [selectedElement, template_content])

  // Handle hover preview (visual only, don't modify item data)
  const handleAnimationHover = (animationType) => {
    setHoveredAnimation(animationType)
    // Note: We're not applying any actual animation changes here
    // This is just for visual feedback in the UI
  }

  // Handle hover leave
  const handleAnimationHoverLeave = () => {
    setHoveredAnimation(null)
    // Note: No need to remove anything since we didn't apply anything
  }



  // Get animation defaults
  const getAnimationDefaults = (animationType) => {
    const animationDefaults = {
      // Category 1: Entrance animations (defaults per spec)
      fadeIn: { type: 'fadeIn', easing: 'easeInOut', startOpacity: 0, endOpacity: 1, _defaultDuration: 0.5 },
      riseUp: { type: 'riseUp', easing: 'easeOut', direction: 'up', distance: 80, fade: true, _defaultDuration: 0.6 },
      slideIn: { type: 'slideIn', easing: 'easeOut', direction: 'left', distance: 200, fade: false, _defaultDuration: 0.5 },
      zoomIn: { type: 'zoomIn', easing: 'easeOut', startScale: 0.5, endScale: 1, fade: true, _defaultDuration: 0.6 },
      popIn: { type: 'popIn', easing: 'easeOut', startScale: 0.2, overshoot: 1.15, endScale: 1, fade: true, _defaultDuration: 0.35 },
      bounceIn: { type: 'bounceIn', easing: 'easeOut', mode: 'scale', intensity: 1.2, bounces: 3, damping: 2, _defaultDuration: 0.8 },
      blurIn: { type: 'blurIn', easing: 'easeOut', startBlur: 12, endBlur: 0, fade: true, _defaultDuration: 0.6 },
      flipIn: { type: 'flipIn', easing: 'easeOut', axis: 'x', startAngle: 90, fade: true, _defaultDuration: 0.7 },
      rotateIn: { type: 'rotateIn', easing: 'easeOut', startAngle: -180, endAngle: 0, turns: 0, fade: true, _defaultDuration: 0.6 },
      typewriter: { type: 'typewriter', easing: 'linear', cps: 20, showCursor: true, _defaultDuration: 1.0 },
      wordReveal: { type: 'wordReveal', easing: 'easeOut', wordDelay: 0.2, _defaultDuration: 1.0 },
      letterReveal: { type: 'letterReveal', easing: 'easeOut', direction: 'up', distance: 20, rotate: 0, startScale: 1, stagger: 0.05, _defaultDuration: 0.8 },

      // Category 2: Emphasis animations (operate on visible text)
      emphasisPulse: { type: 'emphasisPulse', easing: 'easeInOut', minScale: 1, maxScale: 1.15, pulseCount: 2, _defaultDuration: 0.6 },
      shake: { type: 'shake', easing: 'linear', strength: 8, frequency: 12, rotateAmount: 0, damping: 1, direction: 'both', seed: 1, _defaultDuration: 0.4 },
      emphasisBounce: { type: 'emphasisBounce', easing: 'easeOut', mode: 'position', distance: 24, bounces: 3, damping: 2, _defaultDuration: 0.8 },
      shimmer: { type: 'shimmer', easing: 'linear', color: '#ffffff', width: 0.25, brightness: 0.8, speed: 1, angle: 20, repeat: true, _defaultDuration: 1.5 },
      glitchLoop: { type: 'glitchLoop', easing: 'linear', bursts: 4, burstDuration: 0.18, intensity: 8, rgbOffset: 4, flicker: 0.3, seed: 1, _defaultDuration: 1.0 },

      // Category 3: Exit animations (play at the END of the item window)
      fadeOut: { type: 'fadeOut', easing: 'easeInOut', startOpacity: 1, endOpacity: 0, _defaultDuration: 0.5, _atEnd: true },
      slideOut: { type: 'slideOut', easing: 'easeIn', direction: 'left', distance: 200, fade: false, _defaultDuration: 0.5, _atEnd: true },
      zoomOut: { type: 'zoomOut', easing: 'easeIn', startScale: 1, endScale: 0, fade: true, _defaultDuration: 0.5, _atEnd: true },
      blurOut: { type: 'blurOut', easing: 'easeIn', startBlur: 0, endBlur: 12, fade: true, _defaultDuration: 0.6, _atEnd: true },
      dissolve: { type: 'dissolve', easing: 'easeIn', falloff: 1, scatter: 0, _defaultDuration: 1.2, _atEnd: true },

      // Category 4: Loop animations (span the whole item window, continuous loop)
      floating: { type: 'floating', easing: 'linear', axis: 'vertical', amplitude: 10, cycles: 2, rotate: 0 },
      swing: { type: 'swing', easing: 'linear', angle: 8, cycles: 3, damping: 0 },
      wave: { type: 'wave', easing: 'linear', amplitude: 10, cycles: 2, charPhase: 0.15, rotate: 0 },
      breathing: { type: 'breathing', easing: 'linear', minScale: 1, maxScale: 1.03, cycles: 1, opacityVar: 0 },
      trackingLoop: { type: 'trackingLoop', easing: 'linear', minTracking: 0, maxTracking: 12, cycles: 2 },

      // Category 5: Character & word presets
      staggeredRise: { type: 'staggeredRise', easing: 'easeOut', direction: 'up', distance: 40, stagger: 0.05, fade: true, _defaultDuration: 0.8 },
      staggeredZoom: { type: 'staggeredZoom', easing: 'easeOut', startScale: 0.2, stagger: 0.05, overshoot: 0, rotate: 0, _defaultDuration: 0.8 },
      charRotationCascade: { type: 'charRotationCascade', easing: 'easeOut', angle: -90, stagger: 0.05, fade: true, _defaultDuration: 0.8 },
      scatterAssemble: { type: 'scatterAssemble', easing: 'easeOut', distance: 120, rotate: 90, stagger: 0.03, seed: 1, _defaultDuration: 1.2 },
      wordHighlightCascade: { type: 'wordHighlightCascade', easing: 'linear', highlightColor: '#ffd400', scale: 1.15, persist: false },

      // Category 6: Advanced reveals
      trackingReveal: { type: 'trackingReveal', easing: 'easeOut', tracking: 24, stagger: 0.03, _defaultDuration: 0.8 },
      charFlipCascade: { type: 'charFlipCascade', easing: 'easeOut', axis: 'y', startAngle: 90, stagger: 0.05, fade: true, _defaultDuration: 0.8 },
      curtainReveal: { type: 'curtainReveal', easing: 'easeInOut', direction: 'horizontal', softness: 0.05, reverse: false, _defaultDuration: 0.8 },
      liquidReveal: { type: 'liquidReveal', easing: 'easeInOut', direction: 'left', wave: 0.08, frequency: 3, softness: 0.08, _defaultDuration: 1.2 },
      textDrawOn: { type: 'textDrawOn', easing: 'easeInOut', direction: 'left', softness: 0.02, _defaultDuration: 1.5 },
      textBehindObject: { type: 'textBehindObject', easing: 'easeInOut', direction: 'up', softness: 0.04, _defaultDuration: 1.0 },
    }
    return animationDefaults[animationType]
  }

  // Animation definitions with icons and categories
  const animationCategories = {
    'Entrance Animations': [
      { type: 'fadeIn', name: 'Fade In', icon: 'fade.png', description: 'Gradually appear' },
      { type: 'riseUp', name: 'Rise Up', icon: 'ascend.png', description: 'Rise into position' },
      { type: 'slideIn', name: 'Slide In', icon: 'drift.png', description: 'Slide into frame' },
      { type: 'zoomIn', name: 'Zoom In', icon: 'pop.png', description: 'Grow into view' },
      { type: 'popIn', name: 'Pop In', icon: 'pop.png', description: 'Punchy pop appearance' },
      { type: 'bounceIn', name: 'Bounce In', icon: 'bounce.png', description: 'Elastic bounce entrance' },
      { type: 'typewriter', name: 'Typewriter', icon: 'typewriter.png', description: 'Type characters one by one' },
      { type: 'wordReveal', name: 'Word by Word', icon: 'block.png', description: 'Reveal one word at a time' },
      { type: 'letterReveal', name: 'Letter by Letter', icon: 'ascend.png', description: 'Reveal each letter' },
      { type: 'blurIn', name: 'Blur In', icon: 'clarify.png', description: 'Emerge from blur' },
      { type: 'flipIn', name: 'Flip In', icon: 'rotate.png', description: '3D flip into view' },
      { type: 'rotateIn', name: 'Rotate In', icon: 'rotate.png', description: 'Rotate into place' }
    ],
    'Emphasis Animations': [
      { type: 'emphasisPulse', name: 'Pulse', icon: 'pulse.png', description: 'Grow & shrink to draw attention' },
      { type: 'shake', name: 'Shake', icon: 'skate.png', description: 'Rapid vibration' },
      { type: 'emphasisBounce', name: 'Bounce', icon: 'bounce.png', description: 'Bounce in place' },
      { type: 'shimmer', name: 'Shimmer', icon: 'clarify.png', description: 'Moving highlight sweep' },
      { type: 'glitchLoop', name: 'Glitch Loop', icon: 'burst.png', description: 'Digital glitch bursts' }
    ],
    'Exit Animations': [
      { type: 'fadeOut', name: 'Fade Out', icon: 'fade.png', description: 'Gradually disappear' },
      { type: 'slideOut', name: 'Slide Out', icon: 'drift.png', description: 'Slide away' },
      { type: 'zoomOut', name: 'Zoom Out', icon: 'pop.png', description: 'Shrink away' },
      { type: 'blurOut', name: 'Blur Out', icon: 'clarify.png', description: 'Blur out of focus' },
      { type: 'dissolve', name: 'Dissolve', icon: 'burst.png', description: 'Dissolve into background' }
    ],
    'Loop Animations': [
      { type: 'floating', name: 'Floating', icon: 'drift.png', description: 'Gentle drift up and down' },
      { type: 'swing', name: 'Swing', icon: 'rotate.png', description: 'Swing side to side' },
      { type: 'wave', name: 'Wave', icon: 'skate.png', description: 'Characters move in a wave' },
      { type: 'breathing', name: 'Breathing', icon: 'pulse.png', description: 'Slow scale in and out' },
      { type: 'trackingLoop', name: 'Tracking Loop', icon: 'spread.png', description: 'Letter spacing expands & contracts' }
    ],
    'Character & Word': [
      { type: 'staggeredRise', name: 'Staggered Rise', icon: 'ascend.png', description: 'Characters rise one by one' },
      { type: 'staggeredZoom', name: 'Staggered Zoom', icon: 'pop.png', description: 'Characters zoom in sequence' },
      { type: 'charRotationCascade', name: 'Rotation Cascade', icon: 'roll.png', description: 'Characters rotate into place' },
      { type: 'scatterAssemble', name: 'Scatter & Assemble', icon: 'merge.png', description: 'Letters converge from scatter' },
      { type: 'wordHighlightCascade', name: 'Word Highlight', icon: 'clarify.png', description: 'Highlight words one at a time' }
    ],
    'Advanced Reveals': [
      { type: 'trackingReveal', name: 'Tracking Reveal', icon: 'spread.png', description: 'Letters converge from wide spacing' },
      { type: 'charFlipCascade', name: 'Flip Cascade', icon: 'rotate.png', description: 'Characters flip in sequence' },
      { type: 'curtainReveal', name: 'Curtain Reveal', icon: 'block.png', description: 'Open from the center' },
      { type: 'liquidReveal', name: 'Liquid Reveal', icon: 'clarify.png', description: 'Reveal with a wavy edge' },
      { type: 'textDrawOn', name: 'Draw On', icon: 'typewriter.png', description: 'Directional wipe reveal' },
      { type: 'textBehindObject', name: 'Behind Object', icon: 'merge.png', description: 'Emerge from an edge' }
    ]
  }

  const addAnimation = (animationType) => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items||[]).findIndex(it => it.id === selectedElement)
      if (idx >= 0) {
        const it = p.items[idx]

        // Check if this animation is already applied
        const hasThisAnimation = it.animations && it.animations.length > 0 && it.animations[0].type === animationType

        if (hasThisAnimation) {
          // Remove the animation (toggle off)
          it.animations = []
          setSelectedAnimation(null)
        } else {
          // Add/replace with new animation (toggle on)
          const animation = getAnimationDefaults(animationType)
          if (!animation) return

          // Default timing to element time. Entrance presets carry a per-type
          // default duration (_defaultDuration) which we use as the animation
          // window (clamped to the item's own duration), while basic/loop
          // animations span the whole item by default.
          const time = it.time || { start: 0, duration: 2 }
          const { _defaultDuration, _atEnd, ...animClean } = animation
          const itemDuration = time.duration || 2
          const animDuration = _defaultDuration != null
            ? Math.min(_defaultDuration, itemDuration)
            : itemDuration
          // Exit animations (_atEnd) start near the end of the item window so the
          // text animates away as the item finishes. `start` is relative to the
          // item's own start (the engine adds itemTime.start).
          const animStart = _atEnd
            ? Math.max(0, itemDuration - animDuration)
            : 0
          const newAnimation = { ...animClean, start: animStart, duration: animDuration }

          // Only one animation allowed - replace any existing animations
          it.animations = [newAnimation]
          setSelectedAnimation(animationType)
        }
        break
      }
    }
    settemplate_content(next)

    // Don't close the secondary bar - let user try different animations
    // onClose()
  }

  // Add function to remove animation
  const removeAnimation = () => {
    const next = { ...template_content }
    for (const p of next.pages || []) {
      const idx = (p.items||[]).findIndex(it => it.id === selectedElement)
      if (idx >= 0) {
        const it = p.items[idx]
        // Remove all animations
        it.animations = []
        break
      }
    }
    settemplate_content(next)

    // Clear selected animation
    setSelectedAnimation(null)
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Animate</h2>
        </div>
        <div className="flex items-center gap-2">
          {appliedAnimations.length > 0 && (
            <button
              onClick={removeAnimation}
              className="px-2 py-1 text-xs bg-red-100 text-red-600 hover:bg-red-200 rounded-md transition-colors"
            >
              Remove
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Animations Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {Object.entries(animationCategories).map(([category, animations]) => (
          <div key={category} className="mb-6">
            <h3 className="text-sm font-medium text-gray-600 mb-3 uppercase tracking-wide">
              {category}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {animations.map((animation) => {
                const isApplied = appliedAnimations.includes(animation.type)
                const isHovered = hoveredAnimation === animation.type

                return (
                  <button
                    key={animation.type}
                    onClick={() => addAnimation(animation.type)}
                    onMouseEnter={() => handleAnimationHover(animation.type)}
                    onMouseLeave={handleAnimationHoverLeave}
                    className={`group relative rounded-lg p-4 transition-all duration-200 hover:shadow-md bg-gray-50 ${
                      isApplied
                        ? 'border-2 border-green-400'
                        : isHovered
                        ? 'border-2 border-blue-400'
                        : 'border-2 border-gray-200'
                    }`}
                  >
                  {/* Animation Icon */}
                  <div className="flex justify-center mb-2">
                    <img
                      src={`/static_resources/effects/${animation.icon}`}
                      alt={animation.name}
                      className="w-8 h-8 object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                      onError={(e) => {
                        // Fallback to a default icon if image fails to load
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.display = 'flex'
                      }}
                    />
                    {/* Fallback icon */}
                    <div
                      className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-md hidden items-center justify-center"
                      style={{ display: 'none' }}
                    >
                      <span className="text-white text-xs font-bold">
                        {animation.name.charAt(0)}
                      </span>
                    </div>
                  </div>

                  {/* Animation Name */}
                  <div className="text-sm font-medium text-gray-800 transition-colors">
                    {animation.name}
                  </div>

                  {/* Animation Description */}
                  <div className="text-xs mt-1 leading-tight text-gray-500">
                    {animation.description}
                  </div>

                </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-center">
          <p className="text-xs text-gray-500">
            {appliedAnimations.length > 0
              ? 'Hover to preview • Click to replace animation'
              : 'Hover to preview • Click to apply animation'
            }
          </p>
        </div>
      </div>
    </div>
  )
}
